import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import type { User, ContractTemplate } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { FeeTypeManager, type InlineFeeType } from '@/components/FeeTypeManager';
import { TaskSettings } from '@/components/TaskSettings';
import { PaymentSkipSettings } from '@/components/PaymentSkipSettings';
import { trackEvent } from '@/lib/faro';

const COMPOUNDING_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'monthly', label: 'Monthly' },
];

const DAY_OPTIONS = [
    { value: '0', label: 'Monday' },
    { value: '1', label: 'Tuesday' },
    { value: '2', label: 'Wednesday' },
    { value: '3', label: 'Thursday' },
    { value: '4', label: 'Friday' },
    { value: '5', label: 'Saturday' },
    { value: '6', label: 'Sunday' },
];

const ADMIN_FEE_FREQ_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'monthly', label: 'Monthly' },
];

interface FormState {
    title: string;
    opening_balance: string;
    interest_rate: string;
    compounding_frequency: string;
    minimum_payment: string;
    maximum_payment: string;
    payment_due_day: string;
    origination_fee: string;
    funding_source: string;
    admin_fee_amount: string;
    admin_fee_frequency: string;
    min_principal_payoff_pct: string;
    exclusive_dom: boolean;
    allow_adhoc_fees: boolean;
    hardcore_mode: boolean;
    post_fees_to_feed: boolean;
    enable_wheel: boolean;
    enable_dice: boolean;
    enable_tasks: boolean;
    task_mode: 'non_cumulative' | 'cumulative' | 'unlimited';
    allow_editing_while_in_debt: boolean;
    allow_random_assignment: boolean;
    allow_self_selection: boolean;
    penalty_on_abandon: boolean;
    allow_payment_skip: boolean;
    max_skips_allowed: string;
    skip_fee_amount: string;
    skip_resets_annually: boolean;
}

const defaultForm: FormState = {
    title: '',
    opening_balance: '',
    interest_rate: '',
    compounding_frequency: 'weekly',
    minimum_payment: '',
    maximum_payment: '',
    payment_due_day: '6',
    origination_fee: '',
    funding_source: '',
    admin_fee_amount: '',
    admin_fee_frequency: '',
    min_principal_payoff_pct: '',
    exclusive_dom: false,
    allow_adhoc_fees: true,
    hardcore_mode: false,
    post_fees_to_feed: false,
    enable_wheel: false,
    enable_dice: false,
    enable_tasks: false,
    task_mode: 'non_cumulative',
    allow_editing_while_in_debt: true,
    allow_random_assignment: false,
    allow_self_selection: true,
    penalty_on_abandon: false,
    allow_payment_skip: false,
    max_skips_allowed: '2',
    skip_fee_amount: '0',
    skip_resets_annually: true,
};

export default function BespokeContractPage() {
    const { subId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState<FormState>(defaultForm);
    const [feeTypes, setFeeTypes] = useState<InlineFeeType[]>([]);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

    // Fetch sub info
    const { data: subData } = useQuery({
        queryKey: ['profile', subId],
        queryFn: () => api.get<{ data: User }>(`/profiles/${subId}`),
    });

    // Fetch dom's templates for quick-fill
    const { data: templatesData } = useQuery({
        queryKey: ['my-templates'],
        queryFn: () => api.get<{ data: ContractTemplate[] }>('/my-templates'),
    });

    const sub = subData?.data;
    const templates = templatesData?.data ?? [];

    const mutation = useMutation({
        mutationFn: (data: Record<string, unknown>) =>
            api.post<{ data: { id: number } }>('/contracts/bespoke', data),
        onSuccess: (res) => {
            trackEvent('bespoke_contract_created');
            navigate(`/contracts/${res.data.id}`);
        },
        onError: (err: Error) => {
            if (err instanceof ApiError && err.errors) {
                setFieldErrors(err.errors);
            }
            setError(err.message);
        },
    });

    const fillFromTemplate = (template: ContractTemplate) => {
        setSelectedTemplateId(template.id);
        setForm({
            title: template.title,
            opening_balance: template.opening_balance,
            interest_rate: template.interest_rate,
            compounding_frequency: template.compounding_frequency,
            minimum_payment: template.minimum_payment,
            maximum_payment: template.maximum_payment ?? '',
            payment_due_day: template.payment_due_day ?? '6',
            origination_fee: template.origination_fee ?? '',
            funding_source: template.funding_source ?? '',
            admin_fee_amount: template.admin_fee_amount ?? '',
            admin_fee_frequency: template.admin_fee_frequency ?? '',
            min_principal_payoff_pct: template.min_principal_payoff_pct ?? '',
            exclusive_dom: false,
            allow_adhoc_fees: template.allow_adhoc_fees,
            hardcore_mode: template.hardcore_mode,
            post_fees_to_feed: template.post_fees_to_feed,
            enable_wheel: template.enable_wheel ?? false,
            enable_dice: template.enable_dice ?? false,
            enable_tasks: template.enable_tasks ?? false,
            task_mode: template.task_mode ?? 'non_cumulative',
            allow_editing_while_in_debt: template.allow_editing_while_in_debt ?? true,
            allow_random_assignment: template.allow_random_assignment ?? false,
            allow_self_selection: template.allow_self_selection ?? true,
            penalty_on_abandon: template.penalty_on_abandon ?? false,
            allow_payment_skip: (template as unknown as Record<string, unknown>).allow_payment_skip as boolean ?? false,
            max_skips_allowed: String((template as unknown as Record<string, unknown>).max_skips_allowed ?? '2'),
            skip_fee_amount: String((template as unknown as Record<string, unknown>).skip_fee_amount ?? '0'),
            skip_resets_annually: (template as unknown as Record<string, unknown>).skip_resets_annually as boolean ?? true,
        });
        setFeeTypes(
            (template.fee_types ?? []).map((ft) => ({
                name: ft.name,
                category: ft.category,
                default_amount: ft.default_amount,
                fee_trigger: ft.fee_trigger ?? 'manual',
            }))
        );
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        const payload: Record<string, unknown> = {
            sub_id: Number(subId),
            title: form.title,
            opening_balance: parseFloat(form.opening_balance),
            interest_rate: parseFloat(form.interest_rate),
            compounding_frequency: form.compounding_frequency,
            minimum_payment: parseFloat(form.minimum_payment),
            maximum_payment: form.maximum_payment ? parseFloat(form.maximum_payment) : undefined,
            payment_due_day: form.payment_due_day ? parseInt(form.payment_due_day) : undefined,
            origination_fee: form.origination_fee ? parseFloat(form.origination_fee) : 0,
            funding_source: form.funding_source || undefined,
            admin_fee_amount: form.admin_fee_amount ? parseFloat(form.admin_fee_amount) : 0,
            admin_fee_frequency: form.admin_fee_frequency || undefined,
            min_principal_payoff_pct: form.min_principal_payoff_pct ? parseFloat(form.min_principal_payoff_pct) : undefined,
            exclusive_dom: form.exclusive_dom,
            allow_adhoc_fees: form.allow_adhoc_fees,
            hardcore_mode: form.hardcore_mode,
            post_fees_to_feed: form.post_fees_to_feed,
            enable_wheel: form.enable_wheel,
            enable_dice: form.enable_dice,
            enable_tasks: form.enable_tasks,
            task_mode: form.task_mode,
            allow_editing_while_in_debt: form.allow_editing_while_in_debt,
            allow_random_assignment: form.allow_random_assignment,
            allow_self_selection: form.allow_self_selection,
            penalty_on_abandon: form.penalty_on_abandon,
            allow_payment_skip: form.allow_payment_skip,
            max_skips_allowed: parseInt(form.max_skips_allowed),
            skip_fee_amount: parseFloat(form.skip_fee_amount) || 0,
            skip_resets_annually: form.skip_resets_annually,
            fee_types: feeTypes.length > 0 ? feeTypes.map((ft) => ({
                name: ft.name,
                category: ft.category,
                default_amount: parseFloat(ft.default_amount),
                fee_trigger: ft.fee_trigger,
            })) : undefined,
        };

        mutation.mutate(payload);
    };

    const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const toggle = (field: keyof FormState) => () =>
        setForm((f) => ({ ...f, [field]: !f[field] }));

    const fieldError = (field: string) =>
        fieldErrors[field]?.[0] ? (
            <p className="mt-1 text-xs text-red-400">{fieldErrors[field][0]}</p>
        ) : null;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-semibold">
                    New Contract {sub && <span className="text-slate-400">for {sub.display_name}</span>}
                </h1>
                <p className="text-sm text-slate-500">Create a bespoke contract directly for this sub.</p>
            </div>

            {/* Template quick-fill */}
            {templates.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Start from Template</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {templates.map((t) => (
                                <Button
                                    key={t.id}
                                    size="sm"
                                    variant={selectedTemplateId === t.id ? 'default' : 'outline'}
                                    onClick={() => fillFromTemplate(t)}
                                >
                                    {t.title}
                                </Button>
                            ))}
                            <Button
                                size="sm"
                                variant={selectedTemplateId === null ? 'default' : 'ghost'}
                                onClick={() => { setSelectedTemplateId(null); setForm(defaultForm); setFeeTypes([]); }}
                            >
                                Custom
                            </Button>
                        </div>
                        {selectedTemplateId && (
                            <p className="mt-2 text-xs text-slate-500">
                                Terms pre-filled from template. You can modify any field below.
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Contract Terms</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-1 block text-sm text-slate-400">Title</label>
                                <Input value={form.title} onChange={set('title')} placeholder="e.g. Sky's Debt Contract" required />
                                {fieldError('title')}
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Opening Balance ($)</label>
                                    <Input type="number" step="0.01" min="0" value={form.opening_balance} onChange={set('opening_balance')} required />
                                    {fieldError('opening_balance')}
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Origination Fee ($)</label>
                                    <Input type="number" step="0.01" min="0" value={form.origination_fee} onChange={set('origination_fee')} placeholder="0" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Interest Rate (% APR)</label>
                                    <Input type="number" step="0.01" min="0" max="100" value={form.interest_rate} onChange={set('interest_rate')} required />
                                    {fieldError('interest_rate')}
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Compounding</label>
                                    <select value={form.compounding_frequency} onChange={set('compounding_frequency')} className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100">
                                        {COMPOUNDING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Minimum Payment ($)</label>
                                    <Input type="number" step="0.01" min="0" value={form.minimum_payment} onChange={set('minimum_payment')} required />
                                    {fieldError('minimum_payment')}
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Maximum Payment ($)</label>
                                    <Input type="number" step="0.01" min="0" value={form.maximum_payment} onChange={set('maximum_payment')} placeholder="No limit" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Payment Due Day</label>
                                    <select value={form.payment_due_day} onChange={set('payment_due_day')} className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100">
                                        {DAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Funding Source</label>
                                    <Input value={form.funding_source} onChange={set('funding_source')} placeholder="e.g. Cash App" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm text-slate-400">Min Principal Payoff (%)</label>
                                    <Input type="number" step="0.01" min="0" max="100" value={form.min_principal_payoff_pct} onChange={set('min_principal_payoff_pct')} placeholder="e.g. 52" />
                                    {fieldError('min_principal_payoff_pct')}
                                    <p className="mt-1 text-xs text-slate-500">Early payoff penalty triggers if sub pays below this % of opening balance</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Admin Fee</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-sm text-slate-400">Admin Fee Amount ($)</label>
                                <Input type="number" step="0.01" min="0" value={form.admin_fee_amount} onChange={set('admin_fee_amount')} placeholder="0" />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm text-slate-400">Admin Fee Frequency</label>
                                <select value={form.admin_fee_frequency} onChange={set('admin_fee_frequency')} className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100">
                                    {ADMIN_FEE_FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Fee & Tax Types</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <FeeTypeManager feeTypes={feeTypes} onChange={setFeeTypes} />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Options</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.allow_adhoc_fees} onChange={toggle('allow_adhoc_fees')} className="rounded" />
                            Allow ad-hoc fees & taxes
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.exclusive_dom} onChange={toggle('exclusive_dom')} className="rounded" />
                            Exclusive contract (sub cannot sign other contracts)
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.hardcore_mode} onChange={toggle('hardcore_mode')} className="rounded" />
                            Hardcore mode (no sub consent required for changes)
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.post_fees_to_feed} onChange={toggle('post_fees_to_feed')} className="rounded" />
                            Post fees & taxes to public feed
                        </label>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Addons</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.enable_wheel} onChange={toggle('enable_wheel')} className="rounded" />
                            Wheel of Fortune
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.enable_dice} onChange={toggle('enable_dice')} className="rounded" />
                            Dice Roll
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.enable_tasks} onChange={toggle('enable_tasks')} className="rounded" />
                            Tasks
                        </label>
                    </CardContent>
                </Card>

                {form.enable_tasks && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Task Settings</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TaskSettings
                                settings={{
                                    task_mode: form.task_mode,
                                    allow_editing_while_in_debt: form.allow_editing_while_in_debt,
                                    allow_random_assignment: form.allow_random_assignment,
                                    allow_self_selection: form.allow_self_selection,
                                    penalty_on_abandon: form.penalty_on_abandon,
                                }}
                                onChange={(s) => setForm(f => ({ ...f, ...s }))}
                            />
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Payment Skip</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <PaymentSkipSettings
                            allowPaymentSkip={form.allow_payment_skip}
                            maxSkipsAllowed={form.max_skips_allowed}
                            skipFeeAmount={form.skip_fee_amount}
                            skipResetsAnnually={form.skip_resets_annually}
                            onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
                        />
                    </CardContent>
                </Card>

                {error && (
                    <div className="rounded-md bg-red-900/30 p-3 text-sm text-red-400">{error}</div>
                )}

                <div className="flex gap-3">
                    <Button type="submit" disabled={mutation.isPending} className="flex-1">
                        {mutation.isPending ? 'Creating Contract...' : 'Create Contract'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    );
}
